import Flickity from 'flickity'
import { session } from 'helpers/session'

export class RecentlyViewedElement extends HTMLElement {
  private flickity?: Flickity

  connectedCallback() {
    this.initializeRecentlyViewed()
  }

  initializeRecentlyViewed() {
    const checkVisibility = () => {
      const tabPanel = this.closest('[role="tabpanel"]')
      const isInTab = !!tabPanel
      const isTabVisible = !isInTab || !tabPanel.hasAttribute('hidden')

      if (!isInTab) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return
          observer.unobserve(this)
          this.loadRecentlyViewed()
        }, { rootMargin: '0px 0px 200px 0px' })
        observer.observe(this)
      } else if (isTabVisible) {
        this.loadRecentlyViewed()
      } else {
        const tabChangeObserver = new MutationObserver(() => {
          if (!tabPanel.hasAttribute('hidden')) {
            tabChangeObserver.disconnect()
            this.loadRecentlyViewed()
          }
        })
        tabChangeObserver.observe(tabPanel, { attributes: true, attributeFilter: ['hidden'] })
      }
    }

    checkVisibility()
  }

  async loadRecentlyViewed() {
    try {
      // Get the product handles from session storage
      const viewedProducts = session.viewedProducts.get(',')
      if (!viewedProducts || (Array.isArray(viewedProducts) && viewedProducts.length === 0)) {
        this.remove()
        return
      }

      const handles = Array.isArray(viewedProducts) ? viewedProducts : [viewedProducts]
      const limit = parseInt(this.dataset.limit || '10', 10)
      const currentProductHandle = this.dataset.currentProductHandle
      const validHandleRegex = /^[a-z0-9-]+$/
      const filteredHandles = handles.filter(handle => {
        if (handle === currentProductHandle) return false
        if (!validHandleRegex.test(handle)) {
          return false
        }
        return true
      })

      const limitedHandles = filteredHandles.slice(0, limit)
      if (limitedHandles.length === 0) {
        this.remove()
        return
      }

      const productPromises = limitedHandles.map(async (handle) => {
        try {
          // Fetch product card HTML using the card view
          const url = `/products/${handle}?view=card`
          const response = await fetch(url)
          if (!response.ok) {
            return null
          }

          const cardHTML = await response.text()

          const parser = new DOMParser()
          const cardDocument = parser.parseFromString(cardHTML, 'text/html')
          const productCard = cardDocument.querySelector('[data-product-card]') || cardDocument.querySelector('[data-product-id]')
          return productCard
        } catch (error) {
          console.error(`[Recently Viewed] Error fetching product with handle ${handle}:`, error)
          return null
        }
      })

      const productCards = (await Promise.all(productPromises)).filter(card => card !== null)
      if (productCards.length === 0) {
        this.remove()
        return
      }

      this.createCarousel(productCards)
    } catch (error) {
      console.error('Failed to load recently viewed products:', error)
      this.showError()
    }
  }

  createCarousel(productCards: (Element | null)[]) {
    this.innerHTML = ''

    const carousel = document.createElement('div')
    carousel.className = this.dataset.carouselClasses || ''

    productCards.forEach((card) => {
      if (!card) return

      const wrapper = document.createElement('div')
      wrapper.className = this.dataset.productCardClasses || ''
      wrapper.appendChild(card.cloneNode(true))
      carousel.appendChild(wrapper)
    })

    this.appendChild(carousel)
    this.updateLazyLoading()

    let carouselSettings = {}
    try {
      if (this.dataset.carouselSettings) {
        carouselSettings = JSON.parse(this.dataset.carouselSettings)
      }
    } catch (error) {
      console.error('Failed to parse carousel settings:', error)
    }

    const defaultOptions = {
      contain: true
    }

    const options = { ...defaultOptions, ...carouselSettings }

    try {
      this.flickity = new Flickity(carousel, options)

      this.dispatchEvent(new CustomEvent('recently-viewed:loaded', {
        bubbles: true,
        detail: {
          carousel,
          flickity: this.flickity,
          productCount: productCards.length
        }
      }))
    } catch (error) {
      console.error('Failed to initialize carousel:', error)
      this.showError()
    }
  }

  updateLazyLoading() {
    if (typeof window !== 'undefined' && (window as any).lazyLoadInstance) {
      try {
        (window as any).lazyLoadInstance.update()
      } catch (error) {
        console.error('Failed to update lazy loading:', error)
      }
    }
  }

  showError() {
    this.remove()
  }

  disconnectedCallback() {
    if (this.flickity) {
      this.flickity.destroy()
    }
  }
}
